import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { 
  ArrowLeft, Gift, Store, Loader2, Minus, Plus, Mail, User,
  CreditCard, CheckCircle, AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface GiftCertificateTemplate {
  id: number;
  code: string;
  profile: number;
  name: string;
  description: string;
  priority: number;
  image: string;
  active: boolean;
  created: number;
  modified: number;
}

interface Restaurant {
  id: string;
  name: string;
  image?: string;
  eatzyClientId?: string;
}

const DEFAULT_AMOUNTS = [25, 50, 75, 100, 150, 200];

export default function PurchaseGiftCertificate() {
  const { clientId } = useParams();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  const [selectedAmount, setSelectedAmount] = useState<number>(50);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [quantity, setQuantity] = useState(1);
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

  const { data: restaurant, isLoading: restaurantLoading } = useQuery<Restaurant>({
    queryKey: ['/api/restaurants', clientId],
    enabled: !!clientId,
  });

  const { data: templates, isLoading: templatesLoading } = useQuery<GiftCertificateTemplate[]>({
    queryKey: ['/api/gift-certificates/templates', clientId],
    enabled: !!clientId,
    retry: false,
  });

  const purchaseMutation = useMutation({
    mutationFn: async (data: { client: string; cart: any[]; success_url: string; cancel_url: string }) => {
      const response = await apiRequest('POST', '/api/gift-certificates/buy', data);
      return response.json();
    },
    onSuccess: (data) => {
      if (data.checkout_url) {
        setCheckoutUrl(data.checkout_url);
        window.location.href = data.checkout_url;
      } else {
        toast({
          title: "Error",
          description: "Failed to start checkout process",
          variant: "destructive"
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Purchase Failed",
        description: error.message || "Unable to complete purchase",
        variant: "destructive"
      });
    }
  });

  const getMatchingTemplateId = (amount: number): number | null => {
    if (!templates || templates.length === 0) return null;
    const match = templates.find(t => t.active);
    return match ? match.id : templates[0].id;
  };

  const handlePurchase = () => {
    if (!clientId) return;

    const finalAmount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    if (finalAmount < 5 || finalAmount > 500) {
      toast({
        title: "Invalid Amount",
        description: "Gift certificate amount must be between $5 and $500",
        variant: "destructive"
      });
      return;
    }

    if (!recipientEmail || !recipientName) {
      toast({
        title: "Missing Recipient Info",
        description: "Please enter recipient name and email",
        variant: "destructive"
      });
      return;
    }

    if (!senderName || !senderEmail) {
      toast({
        title: "Missing Sender Info",
        description: "Please enter your name and email address",
        variant: "destructive"
      });
      return;
    }

    const templateId = getMatchingTemplateId(finalAmount);
    if (templateId === null) {
      toast({
        title: "Unavailable",
        description: "Gift certificates are not currently available for this restaurant",
        variant: "destructive"
      });
      return;
    }

    setShowConfirmDialog(true);
  };

  const confirmPurchase = () => {
    const finalAmount = customAmount ? parseFloat(customAmount) : selectedAmount;

    const templateId = getMatchingTemplateId(finalAmount);
    if (templateId === null) return;
    
    const cartItems = Array.from({ length: quantity }, () => ({
      gift_certificate_template_id: templateId,
      amount: finalAmount,
      recipient_name: recipientName,
      recipient_email: recipientEmail,
      sender_name: senderName,
      sender_email: senderEmail,
      message: personalMessage,
      delivery_method: 'email' as const,
    }));

    purchaseMutation.mutate({
      client: clientId!,
      cart: cartItems,
      success_url: `${window.location.origin}/profile/gift-certificates?purchased=true`,
      cancel_url: `${window.location.origin}/purchase-gift-certificate/${clientId}?cancelled=true`,
    });
  };

  const finalAmount = customAmount ? parseFloat(customAmount) || 0 : selectedAmount;
  const totalAmount = finalAmount * quantity;

  if (restaurantLoading || templatesLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setLocation(`/restaurant/${clientId}`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">Buy Gift Certificate</h1>
            {restaurant && (
              <p className="text-sm text-muted-foreground">{restaurant.name}</p>
            )}
          </div>
        </div>
      </header>

      <main className="p-4 pb-8 max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Gift className="h-5 w-5 text-orange-500" />
              Select Amount
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {DEFAULT_AMOUNTS.map((amount) => (
                <Button
                  key={amount}
                  variant={selectedAmount === amount && !customAmount ? "default" : "outline"}
                  className={cn(
                    "h-14 text-lg font-semibold",
                    selectedAmount === amount && !customAmount && "bg-orange-500"
                  )}
                  onClick={() => {
                    setSelectedAmount(amount);
                    setCustomAmount("");
                  }}
                >
                  ${amount}
                </Button>
              ))}
            </div>
            
            <div className="space-y-2">
              <Label>Or enter custom amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  min="5"
                  max="500"
                  placeholder="Custom amount"
                  className="pl-8"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                />
              </div>
              <p className="text-xs text-muted-foreground">Minimum $5, Maximum $500</p>
            </div>

            <div className="flex items-center justify-between pt-2">
              <Label className="font-medium">Quantity</Label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-8 text-center font-semibold">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.min(10, quantity + 1))}
                  disabled={quantity >= 10}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-5 w-5 text-orange-500" />
              Recipient Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="recipientName">Recipient Name</Label>
              <Input
                id="recipientName"
                placeholder="Enter recipient's name"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="recipientEmail">Recipient Email</Label>
              <Input
                id="recipientEmail"
                type="email"
                placeholder="recipient@example.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The gift certificate will be sent to this email
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="h-5 w-5 text-orange-500" />
              Sender Details & Message
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="senderName">Your Name</Label>
              <Input
                id="senderName"
                placeholder="Enter your name"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="senderEmail">Your Email</Label>
              <Input
                id="senderEmail"
                type="email"
                placeholder="your@email.com"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Personal Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Add a personal message..."
                rows={3}
                value={personalMessage}
                onChange={(e) => setPersonalMessage(e.target.value)}
                className="resize-none"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground">Amount per certificate</span>
              <span className="font-medium">${finalAmount.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-muted-foreground">Quantity</span>
              <span className="font-medium">x{quantity}</span>
            </div>
            <Separator className="my-3" />
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total</span>
              <span className="text-2xl font-bold text-orange-600">
                ${totalAmount.toFixed(2)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Button 
          className="w-full h-12 text-lg bg-orange-500"
          onClick={handlePurchase}
          disabled={purchaseMutation.isPending || finalAmount < 5}
        >
          {purchaseMutation.isPending ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="h-5 w-5 mr-2" />
              Continue to Payment
            </>
          )}
        </Button>
      </main>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-orange-500" />
              Confirm Purchase
            </DialogTitle>
            <DialogDescription>
              Review your gift certificate purchase before proceeding to payment.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Restaurant</span>
                <span className="font-medium">{restaurant?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium">${finalAmount.toFixed(2)} x {quantity}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Recipient</span>
                <span className="font-medium">
                  {recipientName || "Not specified"}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="font-semibold">Total</span>
                <span className="font-bold text-orange-600">${totalAmount.toFixed(2)}</span>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              You will be redirected to complete your payment securely via Stripe.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmPurchase}
              disabled={purchaseMutation.isPending}
              className="bg-orange-500"
            >
              {purchaseMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Proceed to Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
